import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import api from "@/services/api";

export type UserRole = "admin" | "staff" | "client";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatarUrl?: string;
  company?: string;
  phone?: string;
  job_title?: string;
  jobTitle?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, otp?: string) => Promise<AuthUser>;
  register: (name: string, email: string, password: string, company?: string) => Promise<AuthUser>;
  logout: () => void;
  updateUser: (data: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const accessToken = localStorage.getItem("schm_access_token");
    if (accessToken) {
      api
        .get("/auth/me")
        .then((res) => {
          const u = res.data.data;
          setUser({
            id: u.id,
            name: u.name,
            email: u.email,
            role: u.role,
            avatarUrl: u.avatarUrl,
            company: u.company,
          });
          setToken(accessToken);
        })
        .catch(() => {
          localStorage.removeItem("schm_access_token");
          localStorage.removeItem("schm_refresh_token");
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string, otp?: string) => {
    const res = await api.post("/auth/login", { email, password, otp });
    const { user: u, accessToken, refreshToken } = res.data.data;

    localStorage.setItem("schm_access_token", accessToken);
    localStorage.setItem("schm_refresh_token", refreshToken);

    const userData = {
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      avatarUrl: u.avatarUrl,
      company: u.company,
    };

    setUser(userData);
    setToken(accessToken);

    return userData;
  };

  const register = async (name: string, email: string, password: string, company?: string) => {
    const res = await api.post("/auth/register", {
      name,
      email,
      password,
      company: company || name,
    });
    const { user: u, accessToken, refreshToken } = res.data.data;

    localStorage.setItem("schm_access_token", accessToken);
    localStorage.setItem("schm_refresh_token", refreshToken);

    const userData = {
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      avatarUrl: u.avatarUrl,
      company: u.company,
    };

    setUser(userData);
    setToken(accessToken);

    return userData;
  };

  const logout = () => {
    const refreshToken = localStorage.getItem("schm_refresh_token");
    if (refreshToken) {
      api.post("/auth/logout", { refreshToken }).catch(() => {});
    }
    localStorage.removeItem("schm_access_token");
    localStorage.removeItem("schm_refresh_token");
    setUser(null);
    setToken(null);
  };

  const updateUser = (data: Partial<AuthUser>) => {
    if (!user) return;
    const updated = { ...user, ...data };
    setUser(updated);
  };

  return (
    <AuthContext.Provider value={{ user, token, isLoading, isAuthenticated: !!user, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

